import { useState, useEffect } from "react";
import { X, Timer, Sparkles, Building2, Users2, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const ONE_TIME_PLANS = [
  { id: "starter", backend_id: "starter", name: "Starter", audience: "Einzel-User",
    minutes: 30, price_eur: 19, price: "19 €", note: "Einmal-Kauf", icon: Timer,
    accent: "border-novatus-green/50 hover:border-novatus-green" },
  { id: "topup_60", backend_id: "topup_60", name: "Top-Up 60", audience: "Nachkauf",
    minutes: 60, price_eur: 29, price: "29 €", note: "Einmal-Kauf", icon: Sparkles,
    accent: "border-novatus-green hover:border-novatus-green", highlight: true },
  { id: "topup_120", backend_id: "topup_120", name: "Top-Up 120", audience: "Power-User",
    minutes: 120, price_eur: 49, price: "49 €", note: "Einmal-Kauf", icon: Sparkles,
    accent: "border-novatus-green/50 hover:border-novatus-green" },
];

// Reference rate: Starter package (19€ / 30min = 0.633 €/min). All other
// plans are compared against this to show "X% günstiger".
const BASELINE_PRICE_PER_MIN = 19 / 30;

// German consumer law (Preisangabenverordnung / PAngV) requires gross
// end-prices for B2C. Stripe Tax handles the actual VAT calculation at
// checkout — we only adjust the *displayed* price client-side. Net amounts
// continue to be sent to the backend unchanged.
const VAT_RATE = 0.19;
const toGross = (netEur) => netEur * (1 + VAT_RATE);
const fmtEur = (eur, opts = {}) =>
  eur.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...opts,
  });

const savingsBadge = (price, minutes) => {
  if (!price || !minutes) return null;
  const perMin = price / minutes;
  const savings = Math.round((1 - perMin / BASELINE_PRICE_PER_MIN) * 100);
  if (savings < 5) return null;
  return savings;
};

const SUBSCRIPTION_META = {
  pro:        { icon: Sparkles,   accent: "border-novatus-green/50 hover:border-novatus-green" },
  team:       { icon: Users2,     accent: "border-novatus-green/40 hover:border-novatus-green/70" },
  enterprise: { icon: Building2,  accent: "border-novatus-green/40 hover:border-novatus-green/70" },
};

export const PaywallModal = ({ open, onClose, wallet }) => {
  const [loadingId, setLoadingId] = useState(null);
  const [tab, setTab] = useState("onetime"); // onetime | subscription
  const [subPlans, setSubPlans] = useState([]);
  // Two-step consent required by § 356 Abs. 5 BGB to extinguish the
  // statutory withdrawal right BEFORE Stripe Checkout is launched.
  const [consentImmediate, setConsentImmediate] = useState(false);
  const [consentWithdrawalLoss, setConsentWithdrawalLoss] = useState(false);
  const [consentTried, setConsentTried] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/payments/subscription-plans")
      .then(({ data }) => setSubPlans(data || []))
      .catch(() => { /* non-critical */ });
    // Reset consent when modal re-opens so previous checkout doesn't auto-grant.
    setConsentImmediate(false);
    setConsentWithdrawalLoss(false);
    setConsentTried(false);
  }, [open]);

  if (!open) return null;
  const isNegative = wallet?.is_negative;
  const consentOk = consentImmediate && consentWithdrawalLoss;

  const guard = () => {
    if (!consentOk) {
      setConsentTried(true);
      toast.error("Bitte beide Zustimmungs-Häkchen aktivieren, bevor du fortfährst.");
      return false;
    }
    return true;
  };

  const handleOneTime = async (plan) => {
    if (!guard()) return;
    setLoadingId(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: plan.backend_id,
        origin_url: window.location.origin,
        consent_immediate_start: consentImmediate,
        consent_withdrawal_loss: consentWithdrawalLoss,
      });
      if (!data?.url) throw new Error("Keine Checkout-URL erhalten");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zahlung konnte nicht gestartet werden");
      setLoadingId(null);
    }
  };

  const handleSubscribe = async (plan) => {
    if (!guard()) return;
    setLoadingId(plan.id);
    try {
      const { data } = await api.post("/payments/subscribe", {
        plan_id: plan.id,
        origin_url: window.location.origin,
        consent_immediate_start: consentImmediate,
        consent_withdrawal_loss: consentWithdrawalLoss,
      });
      if (!data?.url) throw new Error("Keine Checkout-URL erhalten");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Abo konnte nicht gestartet werden");
      setLoadingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="paywall-modal"
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto nv-card p-6 sm:p-8 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-md text-novatus-text-3 hover:text-white hover:bg-white/5"
          data-testid="paywall-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-5 max-w-xl">
          <p className="label-eyebrow text-novatus-green">Voice-Training</p>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white mt-2">
            {isNegative
              ? "Dein Trial-Guthaben ist aufgebraucht."
              : "Hol dir mehr Voice-Minuten."}
          </h2>
          <p className="text-novatus-text-2 mt-2 leading-relaxed text-sm">
            Voice-Calls mit der KI sind die wirkungsvollste Trainings-Form — du
            zahlst nur die Minuten, die du tatsächlich sprichst. Text-Trainings
            bleiben dauerhaft kostenlos.
          </p>
          {wallet && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full border border-novatus bg-novatus-card-2 text-novatus-text-2" data-testid="paywall-current-balance">
              <Timer className="w-3.5 h-3.5" />
              Aktuelles Guthaben: {wallet.minutes_balance.toFixed(1)} Min
            </div>
          )}
        </div>

        {/* Tab toggle */}
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setTab("onetime")}
            className={`pill-tab ${tab === "onetime" ? "active" : ""}`}
            data-testid="tab-onetime"
          >
            Einmal-Kauf
          </button>
          <button
            onClick={() => setTab("subscription")}
            className={`pill-tab ${tab === "subscription" ? "active" : ""}`}
            data-testid="tab-subscription"
          >
            Monats-Abo
          </button>
        </div>

        {tab === "onetime" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="onetime-plans">
            {ONE_TIME_PLANS.map((p) => {
              const Icon = p.icon;
              const isLoading = loadingId === p.id;
              const savings = savingsBadge(p.price_eur, p.minutes);
              return (
                <div key={p.id} className={`relative nv-card p-5 border-2 ${p.accent} flex flex-col`} data-testid={`plan-${p.id}`}>
                  {p.highlight && (
                    <span className="absolute -top-2 right-4 text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-novatus-green text-black">
                      Empfohlen
                    </span>
                  )}
                  {savings && !p.highlight && (
                    <span className="absolute -top-2 right-4 text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-400 text-black">
                      −{savings}% pro Min
                    </span>
                  )}
                  <Icon className="w-6 h-6 text-novatus-green mb-3" />
                  <h3 className="font-display font-bold text-white text-lg">{p.name}</h3>
                  <p className="text-xs text-novatus-text-3 mt-0.5">{p.audience}</p>
                  <div className="mt-4">
                    <span className="font-display font-black text-3xl text-white" data-testid={`plan-${p.id}-gross-price`}>
                      {fmtEur(toGross(p.price_eur), { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-xs text-novatus-text-3 ml-1">{p.note}</span>
                  </div>
                  <p className="text-[11px] text-novatus-text-3 mt-1">
                    inkl. 19% MwSt · {fmtEur(p.price_eur)} netto
                  </p>
                  <p className="text-sm text-novatus-text-2 mt-3">
                    <span className="text-white font-bold">{p.minutes} Min</span> Voice-Training
                  </p>
                  <p className="text-[11px] text-novatus-text-3 mt-1 flex-1">
                    ≈ {fmtEur(toGross(p.price_eur) / p.minutes)} / Min (brutto)
                  </p>
                  <button
                    onClick={() => handleOneTime(p)}
                    disabled={isLoading || (loadingId && loadingId !== p.id)}
                    className="btn-green w-full mt-5 h-10 rounded-lg text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid={`select-plan-${p.id}`}
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaufen"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {tab === "subscription" && (
          <div data-testid="subscription-plans">
            {subPlans.length === 0 ? (
              <div className="text-center py-8 text-novatus-text-3 text-sm">
                Lade Abos…
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {subPlans.map((p) => {
                  const meta = SUBSCRIPTION_META[p.id] || { icon: Sparkles, accent: "border-novatus/50" };
                  const Icon = meta.icon;
                  const isLoading = loadingId === p.id;
                  const savings = savingsBadge(p.amount_eur, p.minutes_per_month);
                  return (
                    <div key={p.id} className={`relative nv-card p-5 border-2 ${meta.accent} flex flex-col`} data-testid={`sub-plan-${p.id}`}>
                      {p.id === "pro" && (
                        <span className="absolute -top-2 right-4 text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-novatus-green text-black">
                          Beliebt
                        </span>
                      )}
                      {savings && p.id !== "pro" && (
                        <span className="absolute -top-2 right-4 text-[9px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-400 text-black">
                          −{savings}% pro Min
                        </span>
                      )}
                      <Icon className="w-6 h-6 text-novatus-green mb-3" />
                      <h3 className="font-display font-bold text-white text-lg">{p.label}</h3>
                      <p className="text-xs text-novatus-text-3 mt-0.5">{p.audience}</p>
                      <div className="mt-4">
                        <span className="font-display font-black text-3xl text-white" data-testid={`sub-plan-${p.id}-gross-price`}>
                          {fmtEur(toGross(p.amount_eur), { minimumFractionDigits: 2 })}
                        </span>
                        <span className="text-xs text-novatus-text-3 ml-1">/ Monat</span>
                      </div>
                      <p className="text-[11px] text-novatus-text-3 mt-1">
                        inkl. 19% MwSt · {fmtEur(p.amount_eur)} netto
                      </p>
                      <p className="text-sm text-novatus-text-2 mt-3">
                        <span className="text-white font-bold">{p.minutes_per_month} Min</span> jeden Monat
                      </p>
                      <p className="text-[11px] text-novatus-text-3 mt-1 flex-1">
                        ≈ {fmtEur(toGross(p.amount_eur) / p.minutes_per_month)} / Min (brutto) · Jederzeit kündbar
                      </p>
                      <button
                        onClick={() => handleSubscribe(p)}
                        disabled={isLoading || (loadingId && loadingId !== p.id)}
                        className="btn-green w-full mt-4 h-10 rounded-lg text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid={`select-sub-${p.id}`}
                      >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Abo starten"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div
          className={`mt-6 rounded-xl p-4 sm:p-5 border ${
            consentTried && !consentOk
              ? "border-red-500/60 bg-red-500/5"
              : "border-novatus bg-novatus-card-2"
          }`}
          data-testid="paywall-consent-block"
        >
          <p className="label-eyebrow text-novatus-green">Verbraucher-Hinweis</p>
          <p className="text-xs text-novatus-text-2 mt-2 leading-relaxed">
            Bevor du den Kauf abschließt, bestätige bitte die folgenden Punkte. Sie sind für Verbraucher
            gesetzlich vorgeschrieben (§ 356 Abs. 5 BGB) und bewirken, dass deine Trainings-Minuten
            unmittelbar nach Zahlung freigeschaltet werden:
          </p>
          <label className="flex gap-3 items-start mt-3 cursor-pointer" data-testid="consent-immediate-label">
            <input
              type="checkbox"
              checked={consentImmediate}
              onChange={(e) => setConsentImmediate(e.target.checked)}
              className="mt-1 w-4 h-4 accent-novatus-green flex-shrink-0"
              data-testid="consent-immediate-checkbox"
            />
            <span className="text-xs text-white leading-relaxed">
              Ich stimme ausdrücklich zu, dass die Novatus Sales Academy bereits vor Ablauf der
              Widerrufsfrist mit der Bereitstellung der Trainings-Minuten beginnt.
            </span>
          </label>
          <label className="flex gap-3 items-start mt-2 cursor-pointer" data-testid="consent-withdrawal-label">
            <input
              type="checkbox"
              checked={consentWithdrawalLoss}
              onChange={(e) => setConsentWithdrawalLoss(e.target.checked)}
              className="mt-1 w-4 h-4 accent-novatus-green flex-shrink-0"
              data-testid="consent-withdrawal-checkbox"
            />
            <span className="text-xs text-white leading-relaxed">
              Mir ist bekannt, dass ich mit dieser Zustimmung mein gesetzliches Widerrufsrecht mit
              Beginn der Ausführung verliere.
            </span>
          </label>
          <p className="text-[10px] text-novatus-text-3 mt-3 leading-relaxed">
            Mit Klick auf eine Plan-Auswahl oben akzeptierst du außerdem unsere{" "}
            <a href="/agb" target="_blank" rel="noreferrer" className="text-novatus-green hover:underline">AGB</a>
            {" "}und unsere{" "}
            <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-novatus-green hover:underline">Datenschutzerklärung</a>.
            Die vollständige{" "}
            <a href="/widerruf" target="_blank" rel="noreferrer" className="text-novatus-green hover:underline">Widerrufsbelehrung</a>
            {" "}findest du jederzeit im Footer der Anwendung.
          </p>
        </div>

        <p className="text-[11px] text-novatus-text-3 mt-6 text-center">
          Zahlung per Kreditkarte, SEPA, Apple/Google Pay über Stripe.
          Endpreise inkl. 19% MwSt (Deutschland). Bei B2B-Käufen mit gültiger
          USt-IdNr. wird im Checkout das Reverse-Charge-Verfahren angewandt.
          Rechnung wird automatisch erstellt.
        </p>
      </div>
    </div>
  );
};

export default PaywallModal;
